import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Receipt, Plus, Search, DollarSign, Clock, CheckCircle, AlertCircle,
  Building2, Calendar, MoreVertical, CreditCard
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Bills = () => {
  const { token, loading: authLoading } = useAuth();
  const [bills, setBills] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPayDialogOpen, setIsPayDialogOpen] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [formData, setFormData] = useState({
    vendor_id: '',
    due_date: '',
    category: '',
    notes: '',
    line_items: [{ description: '', quantity: 1, unit_price: 0, tax_rate: 0, amount: 0 }]
  });

  useEffect(() => {
    if (!authLoading && token) {
      fetchBills();
      fetchVendors();
    } else if (!authLoading && !token) {
      setLoading(false);
    }
  }, [token, authLoading, statusFilter]);

  const fetchBills = async () => {
    try {
      const url = statusFilter === 'all' ? `${API}/bills` : `${API}/bills?status=${statusFilter}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBills(response.data);
    } catch (error) {
      toast.error('Failed to fetch bills');
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await axios.get(`${API}/vendors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendors(response.data);
    } catch (error) {
      console.error('Failed to fetch vendors');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/bills`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bill created successfully');
      setIsDialogOpen(false);
      resetForm();
      fetchBills();
    } catch (error) {
      toast.error('Error creating bill');
    }
  };

  const handlePayBill = async () => {
    if (!selectedBill || paymentAmount <= 0) return;

    try {
      await axios.post(`${API}/bills/${selectedBill.id}/pay?amount=${paymentAmount}`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Payment recorded');
      setIsPayDialogOpen(false);
      setSelectedBill(null);
      setPaymentAmount(0);
      fetchBills();
    } catch (error) {
      toast.error('Error recording payment');
    }
  };

  const resetForm = () => {
    setFormData({
      vendor_id: '',
      due_date: '',
      category: '',
      notes: '',
      line_items: [{ description: '', quantity: 1, unit_price: 0, tax_rate: 0, amount: 0 }]
    });
  };

  const addLineItem = () => {
    setFormData({
      ...formData,
      line_items: [...formData.line_items, { description: '', quantity: 1, unit_price: 0, tax_rate: 0, amount: 0 }]
    });
  };

  const updateLineItem = (index, field, value) => {
    const items = [...formData.line_items];
    items[index][field] = value;
    items[index].amount = items[index].quantity * items[index].unit_price * (1 + items[index].tax_rate / 100);
    setFormData({ ...formData, line_items: items });
  };

  const removeLineItem = (index) => {
    const items = formData.line_items.filter((_, i) => i !== index);
    setFormData({ ...formData, line_items: items });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-emerald-500/20 text-emerald-400';
      case 'pending': return 'bg-amber-500/20 text-amber-400';
      case 'overdue': return 'bg-red-500/20 text-red-400';
      case 'partially_paid': return 'bg-blue-500/20 text-blue-400';
      default: return 'bg-slate-500/20 text-slate-400';
    }
  };

  const filteredBills = bills.filter(b => 
    b.vendor_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.bill_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPending = bills.filter(b => b.status === 'pending').reduce((sum, b) => sum + b.total, 0);
  const totalOverdue = bills.filter(b => b.status === 'overdue').reduce((sum, b) => sum + b.total, 0);
  const totalPaid = bills.filter(b => b.status === 'paid').reduce((sum, b) => sum + b.total, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Bills</h1>
          <p className="text-slate-400">Manage bills and payments to vendors</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-indigo-600 hover:bg-indigo-700">
              <Plus className="w-4 h-4 mr-2" />
              Add Bill
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-700 max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">Create New Bill</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Vendor *</Label>
                  <Select 
                    value={formData.vendor_id}
                    onValueChange={(value) => setFormData({...formData, vendor_id: value})}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                      <SelectValue placeholder="Select vendor" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {vendors.map(v => (
                        <SelectItem key={v.id} value={v.id} className="text-white">
                          {v.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300">Due Date *</Label>
                  <Input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                    className="bg-slate-800 border-slate-700 text-white"
                    required
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Category</Label>
                  <Input
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="bg-slate-800 border-slate-700 text-white"
                    placeholder="e.g., Office Supplies"
                  />
                </div>
              </div>

              {/* Line Items */}
              <div>
                <Label className="text-slate-300">Line Items</Label>
                <div className="space-y-2 mt-2">
                  {formData.line_items.map((item, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-center">
                      <Input
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                        className="col-span-4 bg-slate-800 border-slate-700 text-white"
                      />
                      <Input
                        type="number"
                        placeholder="Qty"
                        value={item.quantity}
                        onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                        className="col-span-2 bg-slate-800 border-slate-700 text-white"
                      />
                      <Input
                        type="number"
                        placeholder="Price"
                        value={item.unit_price}
                        onChange={(e) => updateLineItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                        className="col-span-2 bg-slate-800 border-slate-700 text-white"
                      />
                      <Input
                        type="number"
                        placeholder="Tax %"
                        value={item.tax_rate}
                        onChange={(e) => updateLineItem(index, 'tax_rate', parseFloat(e.target.value) || 0)}
                        className="col-span-2 bg-slate-800 border-slate-700 text-white"
                      />
                      <div className="col-span-1 text-white text-sm">
                        MYR {item.amount.toFixed(2)}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeLineItem(index)}
                        className="col-span-1 text-red-400"
                        disabled={formData.line_items.length === 1}
                      >
                        ×
                      </Button>
                    </div>
                  ))}
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addLineItem} className="mt-2">
                  + Add Line
                </Button>
              </div>

              <div>
                <Label className="text-slate-300">Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="bg-slate-800 border-slate-700 text-white"
                  rows={2}
                />
              </div>

              <div className="flex justify-between items-center pt-4 border-t border-slate-700">
                <div className="text-lg font-semibold text-white">
                  Total: MYR {formData.line_items.reduce((sum, item) => sum + item.amount, 0).toFixed(2)}
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">
                    Create Bill
                  </Button>
                </div>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-500/20 rounded-lg">
                <Receipt className="w-5 h-5 text-indigo-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Total Bills</p>
                <p className="text-xl font-bold text-white">{bills.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <Clock className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Pending</p>
                <p className="text-xl font-bold text-white">MYR {totalPending.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500/20 rounded-lg">
                <AlertCircle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Overdue</p>
                <p className="text-xl font-bold text-white">MYR {totalOverdue.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/20 rounded-lg">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Paid</p>
                <p className="text-xl font-bold text-white">MYR {totalPaid.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search bills..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-slate-800/50 border-slate-700 text-white"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 bg-slate-800/50 border-slate-700 text-white">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            <SelectItem value="all" className="text-white">All Status</SelectItem>
            <SelectItem value="pending" className="text-white">Pending</SelectItem>
            <SelectItem value="paid" className="text-white">Paid</SelectItem>
            <SelectItem value="overdue" className="text-white">Overdue</SelectItem>
            <SelectItem value="partially_paid" className="text-white">Partially Paid</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Bills List */}
      {loading ? (
        <div className="text-center py-10">
          <div className="animate-spin w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full mx-auto"></div>
        </div>
      ) : filteredBills.length === 0 ? (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-10 text-center">
            <Receipt className="w-12 h-12 text-slate-500 mx-auto mb-4" />
            <p className="text-slate-400">No bills found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredBills.map((bill) => (
            <motion.div
              key={bill.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="bg-slate-800/50 border-slate-700 hover:border-slate-600 transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-indigo-500/20 rounded-xl flex items-center justify-center">
                        <Receipt className="w-6 h-6 text-indigo-400" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-white">{bill.bill_number}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${getStatusColor(bill.status)}`}>
                            {bill.status}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-400 mt-1">
                          <span className="flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            {bill.vendor_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            Due: {bill.due_date}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-semibold text-white">
                          {bill.currency} {bill.total?.toLocaleString()}
                        </p>
                        {bill.amount_paid > 0 && (
                          <p className="text-sm text-emerald-400">
                            Paid: {bill.currency} {bill.amount_paid?.toLocaleString()}
                          </p>
                        )}
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="w-4 h-4 text-slate-400" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-slate-800 border-slate-700">
                          {bill.status !== 'paid' && (
                            <DropdownMenuItem 
                              className="text-slate-300 hover:text-white"
                              onClick={() => {
                                setSelectedBill(bill);
                                setPaymentAmount(bill.total - bill.amount_paid);
                                setIsPayDialogOpen(true);
                              }}
                            >
                              <CreditCard className="w-4 h-4 mr-2" />
                              Record Payment
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Pay Bill Dialog */}
      <Dialog open={isPayDialogOpen} onOpenChange={setIsPayDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Record Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-slate-400">Bill: {selectedBill?.bill_number}</p>
              <p className="text-slate-400">Vendor: {selectedBill?.vendor_name}</p>
              <p className="text-slate-400">
                Outstanding: {selectedBill?.currency} {((selectedBill?.total || 0) - (selectedBill?.amount_paid || 0)).toLocaleString()}
              </p>
            </div>
            <div>
              <Label className="text-slate-300">Payment Amount</Label>
              <Input
                type="number"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(parseFloat(e.target.value) || 0)}
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPayDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handlePayBill}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Bills;
